class AriaHandler {
	private focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;

	init() {
		const clickableBound = new WeakSet<HTMLElement>();
		const focusHoverableBound = new WeakSet<HTMLElement>();

		const addClickableListeners = (el: HTMLElement) => {
			if (clickableBound.has(el)) {
				return;
			}
			clickableBound.add(el);

			el.addEventListener('click', (e: MouseEvent) => {
				const clicked = e.target as HTMLElement;
				let target = null;
				if (clicked.hasAttribute('aria-controls')) {
					target = e.target as HTMLElement;
				} else {
					target = clicked.closest('button[aria-controls]') as HTMLElement;
				}
				if (null !== target) {
					const targetExpanded =
						'true' === target.getAttribute('aria-expanded') ? 'false' : 'true';
					this.handleChangeAriaControls(target, targetExpanded);
				}
			});
		};

		const addFocusHoverableListeners = (el: HTMLElement) => {
			if (focusHoverableBound.has(el)) {
				return;
			}
			focusHoverableBound.add(el);

			el.addEventListener('focusin', (e: FocusEvent) =>
				this.handleChangeAriaControls(e.currentTarget as HTMLElement, 'true')
			);
			el.addEventListener('focusout', (e: FocusEvent) => {
				if (!el.contains(e.relatedTarget as HTMLElement)) {
					this.handleChangeAriaControls(e.currentTarget as HTMLElement, 'false');
				}
			});

			el.addEventListener('mouseenter', (e: MouseEvent) => {
				this.handleChangeAriaControls(e.target as HTMLElement, 'true');
			});
			el.addEventListener('mouseleave', (e: MouseEvent) => {
				this.handleChangeAriaControls(e.target as HTMLElement, 'false');
			});
		};

		const clickableAriaElements = document.querySelectorAll(
			'button[aria-controls]'
		) as NodeListOf<HTMLElement>;
		clickableAriaElements.forEach((el) => {
			addClickableListeners(el);
		});

		const focusHoverableAriaElements = document.querySelectorAll(
			'li[aria-controls]'
		) as NodeListOf<HTMLElement>;
		focusHoverableAriaElements.forEach((el) => {
			addFocusHoverableListeners(el);
		});

		// Create a MutationObserver to watch for new elements being added to the DOM
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === 'childList') {
					mutation.addedNodes.forEach((node) => {
						if (node instanceof HTMLElement) {
							if (node.matches('button[aria-controls]')) {
								addClickableListeners(node);
							}

							if (node.matches('li[aria-controls]')) {
								addFocusHoverableListeners(node);
							}

							// Also check for any matching descendants
							let descendants = node.querySelectorAll('button[aria-controls]');
							descendants.forEach((descendant) => {
								if (descendant instanceof HTMLElement) {
									addClickableListeners(descendant);
								}
							});

							// Also check for any matching descendants
							descendants = node.querySelectorAll('li[aria-controls]');
							descendants.forEach((descendant) => {
								if (descendant instanceof HTMLElement) {
									addFocusHoverableListeners(descendant);
								}
							});
						}
					});
				}
			});
		});

		// Start observing the document body for changes
		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}

	// Set aria-expanded on target and elements controlled by target.
	handleChangeAriaControls = (target: HTMLElement, targetExpanded: string) => {
		if (target) {
			let allControllers = null;
			const controlId = target.getAttribute('aria-controls');
			if (controlId) {
				const controlledElement = document.getElementById(controlId);
				if (controlledElement) {
					controlledElement.setAttribute('aria-expanded', targetExpanded);
					if ('true' === targetExpanded) {
						controlledElement.removeAttribute('aria-hidden');

						const videoIframes = controlledElement.querySelectorAll(
							'iframe[data-src]'
						) as NodeListOf<HTMLIFrameElement>;
						videoIframes.forEach((iframe) => {
							const src = iframe.dataset.src;
							if (
								src &&
								(src.includes('youtube.com') ||
									src.includes('youtu.be') ||
									src.includes('vimeo.com'))
							) {
								iframe.setAttribute('src', src);
							}
						});

						// Find previously hidden elements within expanded (like close buttons in modals) and make sure they are not hidden.
						const hiddenElements = controlledElement.querySelectorAll(
							'[aria-hidden="true"]'
						) as NodeListOf<HTMLElement>;
						hiddenElements.forEach((el) => {
							if (!this.elIsHidden(el)) {
								el.removeAttribute('aria-hidden');
							}
						});
					}

					if ('false' === targetExpanded) {
						// When closing a modal, check for video iframes and swap src to data-src
						if (
							controlledElement.hasAttribute('aria-modal') &&
							'true' === controlledElement.getAttribute('aria-modal')
						) {
							const videoIframes = controlledElement.querySelectorAll(
								'iframe[src]'
							) as NodeListOf<HTMLIFrameElement>;
							videoIframes.forEach((iframe) => {
								const src = iframe.getAttribute('src');
								if (
									src &&
									(src.includes('youtube.com') ||
										src.includes('youtu.be') ||
										src.includes('vimeo.com'))
								) {
									iframe.removeAttribute('src');
								}
							});
						}
					}

					if (
						controlledElement.hasAttribute('aria-modal') &&
						'true' === controlledElement.getAttribute('aria-modal')
					) {
						if ('true' === targetExpanded) {
							this.trapFocus(controlledElement);
						} else {
							this.releaseFocus();
						}
					}
				}
				allControllers = document.querySelectorAll(
					'[aria-controls="' + controlId + '"]'
				) as NodeListOf<HTMLElement>;
				allControllers.forEach((el) => {
					el.setAttribute('aria-expanded', targetExpanded);
				});
			}
			if (null === allControllers) {
				target.setAttribute('aria-expanded', targetExpanded);
			}
		}
	};

	// Set aria-hidden attributes based on browser width.
	setAriaHidden = () => {
		// if element is hidden, set aria-hidden, unless that elements controller is visible.
		const ariaElements = document.querySelectorAll('[aria-label]') as NodeListOf<HTMLElement>;
		ariaElements.forEach((el) => {
			if (this.elIsHidden(el)) {
				const controller = this.elHasController(el);
				if (controller) {
					el.setAttribute('aria-expanded', 'false');
					controller.setAttribute('aria-expanded', 'false');
				} else {
					el.setAttribute('aria-hidden', 'true');
				}
			} else {
				el.removeAttribute('aria-hidden');
			}
		});

		// if control element is hidden and controlled element is not hidden, remove aria-expanded from controlled element, otherwise apply the same value to the controlled element.
		const ariaControls = document.querySelectorAll(
			'[aria-controls]'
		) as NodeListOf<HTMLElement>;
		ariaControls.forEach((el) => {
			const controlId = el.getAttribute('aria-controls');
			if (controlId) {
				const controlledElement = document.getElementById(controlId);
				if (controlledElement) {
					if (this.elIsHidden(el) && null !== controlledElement.offsetParent) {
						controlledElement.removeAttribute('aria-expanded');
					} else {
						const expanded = el.getAttribute('aria-expanded');
						if (expanded) {
							controlledElement.setAttribute('aria-expanded', expanded);
						}
					}
				}
			}
		});
	};

	// Trap focus within a provided element.
	trapFocus = (el: HTMLElement) => {
		const focusableEls = el.querySelectorAll(
			'iframe, a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), label:not([disabled]), [tabindex]:not([disabled]):not([tabindex="-1"])'
		);
		this.releaseFocus();

		if (!focusableEls.length) {
			return;
		}

		const firstFocusableEl = focusableEls[0] as HTMLElement;
		const lastFocusableEl = focusableEls[focusableEls.length - 1] as HTMLElement;
		firstFocusableEl.focus();

		this.focusTrapHandler = (e: KeyboardEvent) => {
			const isTabPressed = e.key === 'Tab';

			if (!isTabPressed) {
				return;
			}

			if (e.shiftKey) {
				// shift + tab on first
				// eslint-disable-next-line @wordpress/no-global-active-element
				if (document.activeElement === firstFocusableEl) {
					lastFocusableEl.focus();
					e.preventDefault();
				}
				// eslint-disable-next-line @wordpress/no-global-active-element
			} else if (document.activeElement === lastFocusableEl) {
				// tab on last
				firstFocusableEl.focus();
				e.preventDefault();
			}
		};
		document.addEventListener('keydown', this.focusTrapHandler);
	};

	// Remove the active focus trap, if any.
	releaseFocus = () => {
		if (this.focusTrapHandler) {
			document.removeEventListener('keydown', this.focusTrapHandler);
			this.focusTrapHandler = null;
		}
	};

	// Return an elements controller or false.
	elHasController = (el: HTMLElement) => {
		const thisId = el.getAttribute('id');
		const ariaControls = document.querySelector(
			'[aria-controls="' + thisId + '"]'
		) as HTMLElement | null;
		return ariaControls ? ariaControls : false;
	};

	// Return if element is hidden.
	elIsHidden(el: HTMLElement) {
		const style = window.getComputedStyle(el);
		return 'none' === style.display;
	}
}

export default new AriaHandler();
